import { CSSProperties, ReactNode, useLayoutEffect, useState } from 'react';

import { useGridster } from './context';
import { GridsterItemInstance } from './core/gridster-item-instance';
import { GridsterItemConfig, GridsterItemSize } from './types';

export type GridsterItemProps = {
  item: GridsterItemConfig;
  children: (size: GridsterItemSize) => ReactNode;
  className?: string;
  style?: CSSProperties;
};

const EMPTY_SIZE: GridsterItemSize = { x: 0, y: 0, width: 0, height: 0 };

export const GridsterItem: React.FC<GridsterItemProps> = ({ item, children, className, style }) => {
  const gridster = useGridster();
  const [size, setSize] = useState<GridsterItemSize>(EMPTY_SIZE);
  const [store] = useState(() => ({ instance: null as GridsterItemInstance | null }));
  const [setItemEl] = useState(
    () => (el: HTMLDivElement | null) => {
      if (store.instance) {
        gridster.removeItem(store.instance);
        store.instance.destroy();
        store.instance = null;
      }

      if (el) {
        const instance = new GridsterItemInstance(gridster, el, item);
        instance.onSizeChange = setSize;
        store.instance = instance;
        gridster.addItem(instance);
      }
    }
  );

  useLayoutEffect(() => {
    if (store.instance) {
      gridster.updateItem(store.instance, item);
    }
  }, [gridster, item, store]);

  const handles = gridster.options.resizable.enabled
    ? { ...gridster.options.resizable.handles, ...item.resizableHandles }
    : {};

  return (
    <div
      ref={setItemEl}
      className={className ? `gridster-item ${className}` : 'gridster-item'}
      style={style}
    >
      {children(size)}
      {Object.entries(handles).map(([handle, enabled]) =>
        enabled ? (
          <div
            key={handle}
            data-handle={handle}
            className={`gridster-item-resizable-handler handle-${handle}`}
          />
        ) : null
      )}
    </div>
  );
};

GridsterItem.displayName = 'GridsterItem';
